/**
 *
 * UserAndExpertRating
 *
 */
import React from 'react';
import PropTypes from 'prop-types';
import ClassNames from 'classnames';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import Typography from '@material-ui/core/Typography';
import Colors from 'utils/colors';
import Mixpanel from 'utils/mixpanelService';
import RatingTooltip from './RatingTooltip';
import Rating from './Rating';
import { getUserRatingAndCount } from './utils';
import InfoIcon from './images/info.svg';
import GrayInfoIcon from './images/gray-info.svg';

const styles = theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
  },
  text: {
    fontSize: 10,
    marginLeft: 5,
    color: '#3E4543',
  },
  pointer: {
    cursor: 'pointer',
  },

  total: {
    color: Colors.gray,
  },
  icon: {
    fontSize: 15,
  },
  iconWrapper: {
    display: 'flex !important',
    flexDirection: 'row-reverse',
    height: 14,
    alignItems: 'center',
  },
  padded: {
    paddingLeft: 10,
  },

  title: theme.typography.pxSmall,
  rating: {
    display: 'flex',
    alignItems: 'center',
  },
  iconGroup: {
    display: 'flex',
    '& img': {
      marginRight: 7,
    },
  },
  notApplicableText: {
    fontSize: 10,
    color: '#3E4543',
  },
  bigRating: {
    '& p': {
      fontSize: 15,
      marginBottom: 5,
    },
    '& h6': {
      fontSize: 15,
    },
    [theme.breakpoints.down('960')]: {
      margin: '10px 0',
    },
  },

  infoIcon: {
    marginLeft: 8,
    height: 16,
  },

  pageInfoIcon: {
    filter: 'grayscale(1)',
    height: 'auto',
    '&:hover': {
      filter: 'grayscale(0)',
    },
  },
  smallInfoIcon: {
    height: 14,
  },
  titleWithInfo: {
    display: 'flex',
  },
  divider: {
    width: 2,
    height: 50,
    marginRight: 25,
    background: '#ECEFF0',
  },
});

const tooltipDescription = {
  userRating: 'Ratings from users combined with ratings from verified sources.',
  expertRating:
    'The expert rating combines research about people, organizations and expert reviews.',
};
class UserAndExpertRating extends React.Component {
  userRatingClickHandler = () => {
    Mixpanel.track('User Rating Count Clicked', {
      path: window.location.pathname,
    });
    this.smoothScroll('user-reviews-panel');
  };

  smoothScroll = id => {
    const offset = () => (this.props.isMobile ? 200 : 150);
    const $anchor = document.getElementById(id);
    if ($anchor) {
      const offsetTop =
        $anchor.getBoundingClientRect().top + window.pageYOffset;
      window.scroll({
        top: offsetTop - offset(),
        behavior: 'smooth',
      });
    }
  };

  content = (rating, userCount, label, type) => {
    const {
      isBig,
      classes,
      hasUserLink,
      data: { slug },
      withInfo,
      isItemPage,
    } = this.props;
    const tooltipValue =
      type === 'user'
        ? tooltipDescription.userRating
        : tooltipDescription.expertRating;
    return (
      <Grid
        container
        className={ClassNames({
          [classes.bigRating]: isBig,
          [classes.pointer]: type === 'user',
        })}
        onClick={type === 'user' ? this.userRatingClickHandler : null}
      >
        {(isBig || withInfo) && (
          <Grid item xs={12} className={classes.titleWithInfo}>
            <Typography
              className={ClassNames(
                classes.title,
                (isBig || withInfo) && classes.bigTitle,
              )}
              align="left"
              color="textPrimary"
            >
              {label}
            </Typography>
            <RatingTooltip
              tooltipId={`${isBig ? 'big' : 'small'}-${tooltipValue}`}
              position="bottom"
              description={tooltipValue}
            >
              <img
                src={isItemPage ? InfoIcon : GrayInfoIcon}
                className={ClassNames(
                  classes.infoIcon,
                  isItemPage && classes.pageInfoIcon,
                  withInfo && classes.smallInfoIcon,
                )}
                alt="Info"
              />
            </RatingTooltip>
          </Grid>
        )}
        <Rating
          rating={rating}
          type={type}
          isBig={isBig}
          tooltipValue={tooltipValue}
          userRatingCount={userCount}
          hasUserLink={hasUserLink}
          slug={slug}
        />
        {!isBig && !withInfo && (
          <Grid item xs={12}>
            <Typography
              className={classes.title}
              align="left"
              color="textPrimary"
            >
              {label}
            </Typography>
          </Grid>
        )}
      </Grid>
    );
  };

  render() {
    const {
      data,
      data: {
        adjustedExpertRating = undefined,
        calculatedExpertRating,
        slug,
        expertRating,
        blog = false,
      },
      hideExpertRating,
      isBig,
      type,
      classes,
      hideDivider,
      withInfo,
      hideUserRating,
    } = this.props;
    const {
      rating: userFinalRating,
      count: userFinalCount,
    } = getUserRatingAndCount(data, type);

    const hiddenUserRating = (type === 'article' && !isBig) || hideUserRating;
    const hideCardExpertRating =
      (type === 'article' && !_isEmpty(_get(data, 'client'))) || blog;
    const expertRatingFormatted =
      expertRating > 5 ? expertRating / 20 : expertRating;
    const adjustedExpertRatingFormmated =
      adjustedExpertRating > 5
        ? adjustedExpertRating / 20
        : adjustedExpertRating;
    const finalExpertRating =
      adjustedExpertRatingFormmated ||
      calculatedExpertRating ||
      expertRatingFormatted;
    return (
      <Grid container direction="row" justify="flex-start">
        {!hiddenUserRating && (
          <Grid item xs={isBig ? 12 : 7} sm={isBig ? 12 : 7} md={isBig ? 4 : 7}>
            {isBig || withInfo
              ? this.content(
                  userFinalRating,
                  userFinalCount,
                  'User Rating',
                  'user',
                )
              : null}
            {!isBig && !withInfo && (
              <RatingTooltip
                tooltipId={`user-rating-${slug}`}
                position="top"
                description={tooltipDescription.userRating}
              >
                {this.content(
                  userFinalRating,
                  userFinalCount,
                  'User Rating',
                  'user',
                )}
              </RatingTooltip>
            )}
          </Grid>
        )}

        {!hideExpertRating && !hideCardExpertRating && (
          <>
            {isBig && !hideDivider && (
              <Hidden smDown>
                <div className={classes.divider} />
              </Hidden>
            )}
            <Grid
              item
              xs={isBig ? 12 : 5}
              sm={isBig ? 12 : 5}
              md={isBig ? 4 : 5}
            >
              {isBig || withInfo ? (
                this.content(finalExpertRating, null, 'Expert Rating', 'expert')
              ) : (
                <RatingTooltip
                  tooltipId={`expert-rating-${slug}`}
                  position="top"
                  description={tooltipDescription.expertRating}
                >
                  {this.content(finalExpertRating, null, 'Expert Rating')}
                </RatingTooltip>
              )}
            </Grid>
          </>
        )}
      </Grid>
    );
  }
}

UserAndExpertRating.propTypes = {
  expertRating: PropTypes.any,
  classes: PropTypes.object,
  data: PropTypes.object,
  isBig: PropTypes.bool,
  type: PropTypes.string,
  hasUserLink: PropTypes.bool,
  hideExpertRating: PropTypes.bool,
  hideDivider: PropTypes.bool,
  withInfo: PropTypes.bool,
  isItemPage: PropTypes.bool,
  hideUserRating: PropTypes.bool,
  isMobile: PropTypes.bool,
};

UserAndExpertRating.defaultProps = {
  hideDivider: false,
  withInfo: false,
  isItemPage: false,
  hideUserRating: false,
};

export default withStyles(styles)(UserAndExpertRating);
